import type { IImageModelInfo } from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import { Alert, Badge, Button, Group, Paper, PasswordInput, Select, Text } from "@mantine/core"
import { useCallback, useState } from "react"
import { useCompanyApi } from "../../../hooks/useCompanyApi"
import { testImageProvider } from "../../../lib/api"
import { extractErrorMessage } from "../../../lib/api/errors"

interface IImageTestResult {
  valid: boolean
  error?: string
}

interface IProps {
  name: string
  label: string
  settingKey: string
  currentKey: string
  /** A secret is stored server-side but masked — the client never holds it.
   *  Shows a "Configured" badge + placeholder and disables Test (you can't
   *  test a key you don't have). Cleared the moment the user types a new key. */
  configured?: boolean
  onKeyChange: (key: string) => void
  onSave: () => void
  saving: boolean
  comingSoon?: boolean
  /** Show the API-key input + Test/Save (the platform key is super-admin-only, so
   *  tenants hide it and only pick the model). Default true. */
  showKeyInput?: boolean
  /** Disable the model selector (a plain member is read-only). Default false. */
  modelDisabled?: boolean
  /** Models that support reference images — shown in model selector */
  models?: IImageModelInfo[]
  currentModel?: string
  onModelChange?: (model: string) => void
}

export function ImageProviderCard({
  name,
  label,
  currentKey,
  configured = false,
  onKeyChange,
  onSave,
  saving,
  comingSoon = false,
  showKeyInput = true,
  modelDisabled = false,
  models,
  currentModel,
  onModelChange,
}: IProps): React.JSX.Element {
  const company = useCompanyApi()
  const { i18n } = useLingui()
  const [testing, setTesting] = useState(false)
  const [testResult, setTestResult] = useState<IImageTestResult | null>(null)

  // A configured key with no freshly-typed replacement: the saved value is
  // unknown to the client, so Test is meaningless and Save is a no-op.
  const isConfiguredUnchanged = configured && currentKey.trim() === ""

  const handleTest = useCallback(async (): Promise<void> => {
    if (!currentKey.trim() || !company) return
    setTesting(true)
    setTestResult(null)
    try {
      const result = await testImageProvider(company, name, currentKey)
      setTestResult(result)
    } catch (err) {
      setTestResult({ valid: false, error: extractErrorMessage(err) })
    } finally {
      setTesting(false)
    }
  }, [name, currentKey, company])

  return (
    <Paper withBorder p="md">
      <Group justify="space-between" mb="sm">
        <Group gap="xs">
          <Text fw={600}>{label}</Text>
          {comingSoon && (
            <Badge color="gray" variant="outline" size="sm">
              <Trans>Coming soon</Trans>
            </Badge>
          )}
        </Group>
        {showKeyInput && !comingSoon && testResult ? (
          <Badge color={testResult.valid ? "success" : "danger"} variant="dot">
            {testResult.valid ? <Trans>Connected</Trans> : <Trans>Error</Trans>}
          </Badge>
        ) : showKeyInput && !comingSoon && isConfiguredUnchanged ? (
          <Badge color="success" variant="light">
            <Trans>Configured</Trans>
          </Badge>
        ) : showKeyInput && !comingSoon && !currentKey ? (
          <Badge color="gray" variant="outline">
            <Trans>Not configured</Trans>
          </Badge>
        ) : null}
      </Group>
      {showKeyInput && (
        <Group align="end" gap="sm">
          <PasswordInput
            label={i18n._(msg`API key`)}
            placeholder={
              comingSoon
                ? i18n._(msg`Not yet available`)
                : isConfiguredUnchanged
                  ? i18n._(msg`•••• saved key — enter a new one to replace`)
                  : i18n._(msg`Enter ${label} API key`)
            }
            value={currentKey}
            onChange={(e) => onKeyChange(e.currentTarget.value)}
            style={{ flex: 1 }}
            size="sm"
            disabled={comingSoon}
          />
          <Button
            size="sm"
            variant="light"
            onClick={handleTest}
            loading={testing}
            disabled={comingSoon || !currentKey.trim() || !company}
          >
            <Trans>Test</Trans>
          </Button>
          <Button size="sm" onClick={onSave} loading={saving} disabled={comingSoon || !currentKey.trim()}>
            <Trans>Save</Trans>
          </Button>
        </Group>
      )}
      {showKeyInput && testResult?.error && (
        <Alert color="danger" variant="light" mt="sm">
          {testResult.error}
        </Alert>
      )}
      {!comingSoon && models && models.length > 0 && onModelChange && (
        <Select
          mt="sm"
          label={i18n._(msg`Image model`)}
          description={i18n._(msg`Only models that support character reference images are shown`)}
          placeholder={i18n._(msg`Select a model`)}
          value={currentModel ?? null}
          disabled={modelDisabled}
          onChange={(v) => {
            if (v) onModelChange(v)
          }}
          data={models.map((m) => ({
            value: m.id,
            label: m.name,
            description: m.description,
          }))}
          size="sm"
        />
      )}
    </Paper>
  )
}
